/**
 * Pro 2 premium feeling: the "Light Enough to Forget" section from viture.com/pro2 (its "s14").
 * The section pins while scroll drives a one-way timeline: the video settles from 1.06× scale, then the
 * eyebrow, the title word by word, the rule, the three benefits and the 63g weight build in. The
 * timeline never runs backwards. The video fades in as the section arrives and plays once per visit.
 * Timeline positions, durations and easing are taken from the source page.
 */
if (!customElements.get('pro2-feeling')) {
  const MOBILE_QUERY = '(max-width: 640px)';
  /** @type {{ desktop: [number, number], mobile: [number, number] }} Design stage [width, height] */
  const STAGES = { desktop: [1920, 1080], mobile: [375, 812] };
  // Time constant (ms) of the lag behind the scroll position, matching the source's scrub: 0.6
  const SMOOTHING_MS = 200;
  // Title words: the whole set builds over WORD_WINDOW, each word taking WORD_SPAN of it
  const WORD_START = 0.16;
  const WORD_WINDOW = 0.26;
  const WORD_SPAN = 0.6;

  /**
   * @typedef {object} StageParts
   * @property {Element} stage
   * @property {HTMLElement} title
   * @property {HTMLElement[]} words
   * @property {HTMLElement | null} video
   * @property {HTMLElement} eyebrow
   * @property {HTMLElement} line
   * @property {HTMLElement[]} benefits
   * @property {HTMLElement} stat
   */

  /**
   * State set up by connectedCallback. Without a constructor, checkJs types these fields as possibly
   * undefined, so the methods declare `this` as ConnectedFeeling to read them as present.
   * @typedef {object} FeelingState
   * @property {HTMLElement} track
   * @property {HTMLElement} sticky
   * @property {MediaQueryList} mobileQuery
   * @property {boolean} reducedMotion
   * @property {HTMLVideoElement[]} videos
   * @property {number} time Where the timeline is drawn now, easing toward `target`
   * @property {number} target Furthest point on the timeline the scroll position has reached
   * @property {boolean} visible Whether the section was in view on the last scroll check
   * @property {boolean} warmed Whether the videos have started buffering
   * @property {StageParts[]} stages
   * @property {() => void} onResize
   * @property {() => void} onScroll
   * @property {FrameRequestCallback} tick Eases `time` toward `target`, stopping once it catches up
   * @property {number} resizeFrame
   * @property {IntersectionObserver} revealObserver Not created when reduced motion is on
   */

  /** @typedef {Pro2Feeling & FeelingState} ConnectedFeeling */

  /**
   * @param {number} value
   * @returns {number}
   */
  const clamp01 = (value) => Math.min(1, Math.max(0, value));

  /**
   * Progress of a tween that starts at `start` and lasts `duration`, on the section's 0–1 timeline
   * @param {number} time
   * @param {number} start
   * @param {number} duration
   * @returns {number}
   */
  const tweenProgress = (time, start, duration) => clamp01((time - start) / duration);

  /** GSAP's power1.out @param {number} progress */
  const easeOutQuad = (progress) => 1 - (1 - progress) ** 2;
  /** GSAP's power2.out @param {number} progress */
  const easeOutCubic = (progress) => 1 - (1 - progress) ** 3;

  class Pro2Feeling extends HTMLElement {
    /** @this {ConnectedFeeling} */
    connectedCallback() {
      /** @type {HTMLElement} */
      this.track = /** @type {HTMLElement} */ (this.querySelector('.pro2-feeling__track'));
      /** @type {HTMLElement} */
      this.sticky = /** @type {HTMLElement} */ (this.querySelector('.pro2-feeling__sticky'));
      /** @type {MediaQueryList} */
      this.mobileQuery = window.matchMedia(MOBILE_QUERY);
      /** @type {boolean} */
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      /** @type {HTMLVideoElement[]} */
      this.videos = Array.from(this.querySelectorAll('video'));
      /** @type {number} */
      this.time = 0;
      /** @type {number} */
      this.target = 0;
      /** @type {boolean} */
      this.visible = false;
      /** @type {boolean} */
      this.warmed = false;

      /** @type {StageParts[]} */
      this.stages = Array.from(this.querySelectorAll('.pro2-feeling__stage')).map((stage) => {
        const title = /** @type {HTMLElement} */ (stage.querySelector('[data-split]'));
        return {
          stage,
          title,
          words: this.#split(title),
          video: /** @type {HTMLElement | null} */ (stage.querySelector('.pro2-feeling__video')),
          eyebrow: /** @type {HTMLElement} */ (stage.querySelector('.pro2-feeling__eyebrow')),
          line: /** @type {HTMLElement} */ (stage.querySelector('.pro2-feeling__line')),
          benefits: /** @type {HTMLElement[]} */ (Array.from(stage.querySelectorAll('.pro2-feeling__benefit'))),
          stat: /** @type {HTMLElement} */ (stage.querySelector('.pro2-feeling__stat')),
        };
      });

      /** @type {() => void} */
      this.onResize = () => {
        cancelAnimationFrame(this.resizeFrame);
        /** @type {number} */
        this.resizeFrame = requestAnimationFrame(() => {
          this.#fit();
          this.stages.forEach(({ title }) => this.#paintGradient(title));
          this.onScroll();
        });
      };
      /** @type {() => void} */
      this.onScroll = () => {
        this.#videos();
        if (this.reducedMotion) return;
        this.target = Math.max(this.target, this.#scrollTime());
        if (!this.raf) this.raf = requestAnimationFrame(this.tick);
      };
      /** @type {FrameRequestCallback} */
      this.tick = (now) => {
        const elapsed = Math.min(now - (this.lastTick ?? now), 64);
        /** @type {number | null} */
        this.lastTick = now;
        this.time += (this.target - this.time) * (1 - Math.exp(-elapsed / SMOOTHING_MS));
        if (Math.abs(this.target - this.time) < 0.0005) this.time = this.target;
        this.#render();
        if (this.time === this.target) {
          /** @type {number | null} */
          this.raf = null;
          this.lastTick = null;
        } else {
          this.raf = requestAnimationFrame(this.tick);
        }
      };

      this.#fit();
      window.addEventListener('resize', this.onResize);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.mobileQuery.addEventListener('change', this.onResize);
      document.fonts?.ready.then(() => this.stages.forEach(({ title }) => this.#paintGradient(title)));

      if (this.reducedMotion) {
        this.videos.forEach((video) => (video.preload = 'none'));
      } else {
        this.classList.add('is-armed');
        this.#render();
        /** @type {IntersectionObserver} */
        this.revealObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.classList.add('is-revealed');
            this.revealObserver.disconnect();
          },
          { rootMargin: '0px 0px -22% 0px' }
        );
        this.revealObserver.observe(this);
      }
      this.onScroll();
    }

    /** @this {ConnectedFeeling} */
    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      this.mobileQuery.removeEventListener('change', this.onResize);
      this.revealObserver?.disconnect();
      cancelAnimationFrame(this.resizeFrame);
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
    }

    /** @this {ConnectedFeeling} */
    #scrollTime() {
      const rect = this.track.getBoundingClientRect();
      const distance = rect.height - this.sticky.clientHeight;
      return distance > 0 ? clamp01(-rect.top / distance) : rect.top <= 0 ? 1 : 0;
    }

    /**
     * Desktop covers the viewport; mobile is 375 wide and as tall as the viewport at that width
     * @this {ConnectedFeeling}
     */
    #fit() {
      const width = window.innerWidth || this.sticky.clientWidth;
      const height = this.sticky.clientHeight;
      if (this.mobileQuery.matches) {
        const scale = width / STAGES.mobile[0];
        this.style.setProperty('--stage-h', (height / scale).toFixed(2));
        this.style.setProperty('--scale', scale.toFixed(4));
      } else {
        const [stageWidth, stageHeight] = STAGES.desktop;
        this.style.setProperty('--stage-h', String(stageHeight));
        this.style.setProperty('--scale', Math.max(width / stageWidth, height / stageHeight).toFixed(4));
      }
    }

    /**
     * Wrap each word of the title in a span, keeping line breaks
     * @this {ConnectedFeeling}
     * @param {HTMLElement} title
     * @returns {HTMLElement[]}
     */
    #split(title) {
      /** @type {HTMLElement[]} */
      const words = [];
      Array.from(title.childNodes).forEach((node) => {
        if (node.nodeType !== Node.TEXT_NODE || !(/** @type {string} */ (node.nodeValue)).trim()) return;
        const fragment = document.createDocumentFragment();
        (/** @type {string} */ (node.nodeValue)).split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) {
            fragment.append(document.createTextNode(part));
            return;
          }
          const span = document.createElement('span');
          span.className = 'pro2-feeling__word';
          span.textContent = part;
          fragment.append(span);
          words.push(span);
        });
        node.replaceWith(fragment);
      });
      title.classList.add('is-split');
      this.#paintGradient(title);
      return words;
    }

    /**
     * Give each word its slice of one gradient spanning the whole title
     * @param {HTMLElement} title
     */
    #paintGradient(title) {
      if (!title.offsetWidth) return;
      const size = `${title.offsetWidth}px 100%`;
      /** @type {NodeListOf<HTMLElement>} */ (title.querySelectorAll('.pro2-feeling__word')).forEach((word) => {
        word.style.backgroundSize = size;
        word.style.backgroundPosition = `${-(word.offsetLeft - title.offsetLeft)}px 0`;
      });
    }

    /** @this {ConnectedFeeling} */
    #render() {
      const time = this.time;
      this.stages.forEach((parts) => {
        if (parts.video) parts.video.style.transform = `scale(${1.06 - 0.06 * easeOutQuad(tweenProgress(time, 0, 0.9))})`;

        const eyebrowProgress = easeOutCubic(tweenProgress(time, 0.1, 0.14));
        parts.eyebrow.style.opacity = String(0.7 * eyebrowProgress);
        parts.eyebrow.style.transform = `translateY(${30 * (1 - eyebrowProgress)}px)`;

        parts.title.style.opacity = String(tweenProgress(time, WORD_START, 0.05));
        const wordDuration = WORD_WINDOW * WORD_SPAN;
        const stagger = parts.words.length > 1 ? (WORD_WINDOW - wordDuration) / (parts.words.length - 1) : 0;
        parts.words.forEach((word, index) => {
          const progress = easeOutCubic(tweenProgress(time, WORD_START + stagger * index, wordDuration));
          word.style.opacity = String(progress);
          word.style.top = `${34 * (1 - progress)}px`;
        });

        const lineProgress = tweenProgress(time, 0.36, 0.16);
        parts.line.style.opacity = String(lineProgress);
        parts.line.style.transform = `scaleX(${lineProgress})`;

        parts.benefits.forEach((benefit, index) => {
          const progress = easeOutCubic(tweenProgress(time, 0.5 + 0.1 * index, 0.16));
          benefit.style.opacity = String(progress);
          benefit.style.transform = `translateY(${30 * (1 - progress)}px)`;
        });

        const statProgress = easeOutCubic(tweenProgress(time, 0.78, 0.18));
        parts.stat.style.opacity = String(statProgress);
        parts.stat.style.transform = `translateY(${44 * (1 - statProgress)}px)`;
      });
    }

    /**
     * Buffer the video a screen ahead; play it from the start each time the section comes into view
     * @this {ConnectedFeeling}
     */
    #videos() {
      if (this.reducedMotion || !this.videos.length) return;
      const viewportHeight = window.innerHeight;
      const rect = this.getBoundingClientRect();
      const near = rect.bottom > -viewportHeight && rect.top < 2 * viewportHeight;
      const visible = rect.bottom > 0 && rect.top < viewportHeight;
      this.videos.forEach((video) => {
        if (near && !this.warmed) {
          video.preload = 'auto';
          video.load();
        }
        if (visible && !this.visible && video.offsetParent !== null) {
          video.currentTime = 0;
          video.play().catch(() => {});
        } else if (!visible && !video.paused) {
          video.pause();
        }
      });
      if (near) this.warmed = true;
      this.visible = visible;
    }
  }

  customElements.define('pro2-feeling', Pro2Feeling);
}
